'use client';

import React, { useState } from 'react';
import { ChecklistComponent } from '../types/content-components.types.js';
import { Check } from 'lucide-react';

interface ChecklistProps {
  component: ChecklistComponent;
}

export const Checklist: React.FC<ChecklistProps> = ({ component }) => {
  const { props } = component;
  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const toggleItem = (itemId: string) => {
    if (!props.checkable) return;
    
    setCheckedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const progress = props.showProgress
    ? Math.round((checkedItems.length / props.items.length) * 100)
    : 0;

  const getVariantColors = () => {
    switch (props.variant) {
      case 'primary':
        return { bg: 'bg-learn-sage-50', border: 'border-learn-sage-200', check: 'bg-learn-sage-500', progress: 'bg-learn-sage-500' };
      case 'success':
        return { bg: 'bg-green-50', border: 'border-green-200', check: 'bg-green-500', progress: 'bg-green-500' };
      case 'highlight':
        return { bg: 'bg-learn-coral-50', border: 'border-learn-coral-200', check: 'bg-learn-coral-500', progress: 'bg-learn-coral-500' };
      default:
        return { bg: 'bg-gray-50', border: 'border-gray-200', check: 'bg-gray-500', progress: 'bg-gray-500' };
    }
  };

  const colors = getVariantColors();

  const animationDelay = (index: number) => {
    return props.animation === 'stagger' ? `${index * 100}ms` : '0ms';
  };

  return (
    <div className="w-full">
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}

      {/* Progress Bar */}
      {props.showProgress && props.checkable && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-semibold text-gray-900">{progress}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${colors.progress} transition-all duration-300`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Checklist Items */}
      <div className="space-y-2">
        {props.items.map((item, index) => {
          const isChecked = checkedItems.includes(item.id);
          
          return (
            <div
              key={item.id}
              className={`${colors.bg} border ${colors.border} rounded-lg p-4 transition-all duration-200 animate-fadeIn`}
              style={{ animationDelay: animationDelay(index) }}
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                {props.checkable !== false && (
                  <div
                    className={`flex-shrink-0 w-6 h-6 rounded border-2 transition-all duration-200 ${
                      isChecked
                        ? `${colors.check} border-transparent`
                        : 'border-gray-300 bg-white hover:border-gray-400'
                    } flex items-center justify-center cursor-pointer`}
                  >
                    {isChecked && <Check className="w-4 h-4 text-white" />}
                  </div>
                )}

                {/* Content */}
                <div className="flex-1">
                  <p className={`text-sm font-medium transition-all duration-200 ${
                    isChecked ? 'text-gray-600 line-through' : 'text-gray-900'
                  }`}>
                    {item.text}
                  </p>
                  {item.detail && (
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      {item.detail}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

